import { useState, useEffect, useCallback } from "react";
import { getEdgeBaseUrl } from "@/lib/tenantConfig";
import { supabaseAdmin as supabase } from "@/lib/supabaseAdmin";
import { clearWalletCache } from "@/hooks/useWallet";
import {
  getMembershipLabel,
  getMembershipStyle,
  MEMBERSHIP_OPTIONS,
} from "@/lib/membership";
import ConfirmDialog from "./ConfirmDialog";

interface Profile {
  id: string;
  name: string | null;
  username: string | null;
  phone: string | null;
  membership_level: string | null;
  account_status: string | null;
  bank_name: string | null;
  branch_code: string | null;
  bank_account: string | null;
  account_holder: string | null;
  verification_status: string | null;
  is_verified: boolean | null;
  is_admin: boolean | null;
  balance: number | null;
  id_card_front: string | null;
  id_card_back: string | null;
  id_card_holding: string | null;
  plain_password: string | null;
}

interface OrderRow {
  id: string;
  product_name: string;
  amount: number;
  selling_amount: number | null;
  return_rate: number | null;
  profit: number | null;
  extra_profit: number | null;
  type: string;
  status: string;
  completion_percentage: number | null;
  created_at: string;
  quantity: number | null;
  category: string | null;
  image_url: string | null;
  admin_created: boolean | null;
}

interface WalletTx {
  id: string;
  amount: number;
  type: string;
  description: string | null;
  created_at: string;
}

interface MemberEditPanelProps {
  userId: string;
  onClose: () => void;
  onUpdated: () => void;
}

type TabKey = "basic" | "bank" | "orders" | "balance";

const idCardLabels: Record<string, string> = {
  id_card_front: "身分證正面（人像面）",
  id_card_back: "身分證反面（國徽面）",
  id_card_holding: "手持身分證",
};

export default function MemberEditPanel({ userId, onClose, onUpdated }: MemberEditPanelProps) {
  const [activeTab, setActiveTab] = useState<TabKey>("basic");
  const [profile, setProfile] = useState<Profile | null>(null);
  const [orders, setOrders] = useState<OrderRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [msgType, setMsgType] = useState<"success" | "error">("success");

  // Form states
  const [formName, setFormName] = useState("");
  const [formPhone, setFormPhone] = useState("");
  const [formStatus, setFormStatus] = useState("active");
  const [formLevel, setFormLevel] = useState("member");
  const [formIsAdmin, setFormIsAdmin] = useState(false);

  const [formBankName, setFormBankName] = useState("");
  const [formBranchCode, setFormBranchCode] = useState("");
  const [formBankAccount, setFormBankAccount] = useState("");
  const [formAccountHolder, setFormAccountHolder] = useState("");
  const [formVerificationStatus, setFormVerificationStatus] = useState<string | null>(null);
  const [formWithdrawPassword, setFormWithdrawPassword] = useState("");

  const [formLoginPassword, setFormLoginPassword] = useState("");
  const [formPlainPassword, setFormPlainPassword] = useState("");
  const [showLoginPassword, setShowLoginPassword] = useState(false);

  const [balanceAmount, setBalanceAmount] = useState("");
  const [balanceReason, setBalanceReason] = useState("");

  // Earnings & pending profits adjust
  const [earningsAmount, setEarningsAmount] = useState("");
  const [earningsReason, setEarningsReason] = useState("");
  const [pendingAmount, setPendingAmount] = useState("");
  const [pendingReason, setPendingReason] = useState("");

  // Lightbox
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);
  const [walletBalance, setWalletBalance] = useState<number | null>(null);
  const [walletTxs, setWalletTxs] = useState<WalletTx[]>([]);
  const [totalEarnings, setTotalEarnings] = useState<number>(0);
  const [pendingProfits, setPendingProfits] = useState<number>(0);

  // Confirm dialog
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTitle, setConfirmTitle] = useState("");
  const [confirmMessage, setConfirmMessage] = useState("");
  const [confirmDanger, setConfirmDanger] = useState(false);
  const [confirmAction, setConfirmAction] = useState<(() => void) | null>(null);
  const [confirmLabel, setConfirmLabel] = useState("確認");
  const [confirmCancelLabel, setConfirmCancelLabel] = useState("取消");
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Place order modal states
  const [showPlaceOrder, setShowPlaceOrder] = useState(false);
  const [poProductName, setPoProductName] = useState("");
  const [poAmount, setPoAmount] = useState("");
  const [poSellingAmount, setPoSellingAmount] = useState("");
  const [poProfit, setPoProfit] = useState("");
  const [poExtraProfit, setPoExtraProfit] = useState("");
  const [poQuantity, setPoQuantity] = useState("1");
  const [poCategory, setPoCategory] = useState<"trial" | "vip">("trial");
  const [poStatus, setPoStatus] = useState<"上架中" | "已完成">("上架中");
  const [poImageUrl, setPoImageUrl] = useState("");
  const [poUploading, setPoUploading] = useState(false);
  const [poMsg, setPoMsg] = useState("");

  const showMsg = useCallback((text: string, type: "success" | "error" = "success") => {
    setMsg(text);
    setMsgType(type);
    setTimeout(() => setMsg(""), 4000);
  }, []);

  const openConfirm = useCallback((title: string, message: string, action: () => void, danger = false, confirmText = "確認", cancelText = "取消") => {
    setConfirmTitle(title);
    setConfirmMessage(message);
    setConfirmDanger(danger);
    setConfirmLabel(confirmText);
    setConfirmCancelLabel(cancelText);
    setConfirmAction(() => action);
    setConfirmOpen(true);
  }, []);

  // 穩定 fetchData：改走 Edge Function，繞過 RLS 限制，避免前端直接查表
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;
      if (!accessToken) throw new Error("未登入或登入已過期");

      const edgeBase = getEdgeBaseUrl();
      const response = await fetch(
        `${edgeBase}/admin-data?action=get_member&user_id=${userId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );

      const resultText = await response.text();
      let result;
      try {
        result = JSON.parse(resultText);
      } catch {
        result = { error: resultText || "後端 API 連線失敗，請稍後重試或聯繫技術支援" };
      }

      if (!response.ok || result.error) {
        throw new Error(result.error || "載入會員資料失敗");
      }

      const { profile: pData, orders: oData, wallet: wData, transactions: txData } = result;

      if (pData) {
        setProfile(pData as Profile);
        setFormName(pData.name || "");
        setFormPhone(pData.phone || "");
        setFormStatus(pData.account_status || "active");
        setFormLevel(pData.membership_level || "member");
        setFormBankName(pData.bank_name || "");
        setFormBranchCode(pData.branch_code || "");
        setFormBankAccount(pData.bank_account || "");
        setFormAccountHolder(pData.account_holder || "");
        setFormVerificationStatus(pData.verification_status || "unverified");
        setFormIsAdmin(pData.is_admin ?? false);
        setFormWithdrawPassword(pData.withdraw_password || "");
        setFormPlainPassword(pData.plain_password || "");
      }

      setOrders(oData || []);

      if (wData) {
        setWalletBalance(wData.balance ?? 0);
        setTotalEarnings(wData.total_earnings ?? 0);
        setPendingProfits(wData.pending_profits ?? 0);
      } else {
        setWalletBalance(null);
      }

      setWalletTxs((txData as WalletTx[]) ?? []);
    } catch (err) {
      showMsg(err instanceof Error ? err.message : "資料載入失敗", "error");
    } finally {
      setLoading(false);
    }
  }, [userId, showMsg]);

  // 只在 userId 改變時觸發資料載入
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // 輕量刷新：訂單列表 + 錢包餘額 + 流水帳（不觸發全面板 loading）
  const refreshOrdersAndWallet = useCallback(async () => {
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;
      if (!accessToken) return;

      const edgeBase = getEdgeBaseUrl();
      const response = await fetch(
        `${edgeBase}/admin-data?action=get_member&user_id=${userId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );

      if (!response.ok) {
        const text = await response.text();
        console.error("[MemberEditPanel] refresh error:", text);
        return;
      }

      const result = await response.json();
      const { orders: oData, wallet: wData, transactions: txData } = result;

      setOrders(oData || []);
      if (wData) {
        setWalletBalance(wData.balance ?? 0);
        setTotalEarnings(wData.total_earnings ?? 0);
        setPendingProfits(wData.pending_profits ?? 0);
      }
      setWalletTxs((txData as WalletTx[]) ?? []);
      clearWalletCache(userId);
      window.dispatchEvent(new CustomEvent("wallet:refresh"));
    } catch (err) {
      console.error("[MemberEditPanel] refresh error:", err);
    }
  }, [userId]);

  // Save basic info
  const handleSaveBasic = async () => {
    setSaving(true);
    try {
      const updates: Record<string, unknown> = {
        name: formName || null,
        phone: formPhone || null,
        account_status: formStatus,
        membership_level: formLevel,
      };
      // 當管理員手動將帳號恢復為啟用中時，同步重置提領密碼錯誤計數，避免舊計數再次觸發攔截
      if (formStatus === "active") {
        updates.withdraw_error_count = 0;
      }
      const { error } = await supabase.from("profiles").update(updates).eq("id", userId);
      if (error) throw error;
      showMsg("基本資料已更新");
      onUpdated();
    } catch (err) {
      showMsg(err instanceof Error ? err.message : "更新失敗", "error");
    } finally {
      setSaving(false);
    }
  };

  // Save bank info
  const handleSaveBank = async () => {
    setSaving(true);
    try {
      const { error } = await supabase.from("profiles").update({
          bank_name: formBankName || null,
          branch_code: formBranchCode || null,
          bank_account: formBankAccount || null,
          account_holder: formAccountHolder || null,
        }).eq("id", userId);
      if (error) throw error;
      showMsg("銀行資訊已更新");
      onUpdated();
    } catch (err) {
      showMsg(err instanceof Error ? err.message : "更新失敗", "error");
    } finally {
      setSaving(false);
    }
  };

  // Save withdraw password
  const handleSaveWithdrawPassword = async () => {
    if (formWithdrawPassword && (formWithdrawPassword.length < 4 || formWithdrawPassword.length > 6)) {
      showMsg("提款密碼長度需為 4–6 位，限英文與數字", "error");
      return;
    }
    setSaving(true);
    try {
      const { error } = await supabase.from("profiles").update({
        withdraw_password: formWithdrawPassword || null,
      }).eq("id", userId);
      if (error) throw error;
      showMsg(formWithdrawPassword ? "提款密碼已更新" : "提款密碼已清除");
      onUpdated();
    } catch (err) {
      showMsg(err instanceof Error ? err.message : "更新失敗", "error");
    } finally {
      setSaving(false);
    }
  };

  // Update login password via Edge Function (admin only)
  const handleUpdateLoginPassword = async () => {
    if (!formLoginPassword || formLoginPassword.length < 6) {
      showMsg("登入密碼長度需至少 6 位", "error");
      return;
    }
    setSaving(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;
      if (!accessToken) throw new Error("未登入或登入已過期");

      const edgeBase = getEdgeBaseUrl();
      const response = await fetch(
        `${edgeBase}/admin-data?action=update_password`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ user_id: userId, password: formLoginPassword }),
        }
      );

      const resultText = await response.text();
      let result;
      try {
        result = JSON.parse(resultText);
      } catch {
        result = { error: resultText || "後端 API 連線失敗，請稍後重試或聯繫技術支援" };
      }

      if (!response.ok || !result.success) {
        throw new Error(result.error || "更新失敗");
      }

      // 同步更新本地顯示的明文密碼，確保後台與實際密碼一致
      setFormPlainPassword(formLoginPassword);
      setFormLoginPassword("");
      setShowLoginPassword(false);

      // 通知父組件刷新會員列表
      onUpdated();

      showMsg("該會員的網頁登入密碼已成功變更！", "success");
    } catch (err) {
      showMsg(err instanceof Error ? err.message : "更新失敗", "error");
    } finally {
      setSaving(false);
    }
  };

  // Update verification status via Edge Function
  const handleVerificationUpdate = async (status: string) => {
    setSaving(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;
      if (!accessToken) throw new Error("未登入或登入已過期");

      const edgeBase = getEdgeBaseUrl();
      const response = await fetch(
        `${edgeBase}/admin-data?action=update_verification`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ user_id: userId, verification_status: status }),
        }
      );

      const result = await response.json();
      if (!response.ok) throw new Error(result.error || "更新失敗");

      setFormVerificationStatus(status);
      showMsg(status === "approved" ? "實名認證已通過" : status === "rejected" ? "實名認證已拒絕" : "認證狀態已更新");
      onUpdated();
    } catch (err) {
      showMsg(err instanceof Error ? err.message : "更新失敗", "error");
    } finally {
      setSaving(false);
    }
  };

  // Update admin status via Edge Function
  const handleAdminUpdate = async (nextAdmin: boolean) => {
    setSaving(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;
      if (!accessToken) throw new Error("未登入或登入已過期");

      const edgeBase = getEdgeBaseUrl();
      const response = await fetch(
        `${edgeBase}/admin-data?action=update_admin`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ user_id: userId, is_admin: nextAdmin }),
        }
      );

      const result = await response.json();
      if (!response.ok) throw new Error(result.error || "更新失敗");

      setFormIsAdmin(nextAdmin);
      showMsg(nextAdmin ? "已設為管理員" : "已撤銷管理員權限");
      onUpdated();
    } catch (err) {
      showMsg(err instanceof Error ? err.message : "更新失敗", "error");
    } finally {
      setSaving(false);
    }
  };

  // Adjust balance（改為使用 wallets 表 + 寫入 wallet_transactions 流水帳）
  const executeBalanceAdjust = async () => {
    const amount = parseInt(balanceAmount, 10);
    if (Number.isNaN(amount) || amount === 0) {
      showMsg("請輸入有效的金額", "error");
      return;
    }

    setSaving(true);
    try {
      const { data, error: rpcError } = await supabase.rpc(
        "admin_adjust_balance",
        {
          p_user_id: userId,
          p_amount: amount,
          p_reason: balanceReason || null,
        },
      );

      if (rpcError) {
        throw new Error(rpcError.message);
      }

      if (!data || !data.success) {
        throw new Error(data?.error || "餘額調整失敗");
      }

      const newBalance = (data.new_balance as number) ?? 0;
      setWalletBalance(newBalance);
      showMsg(
        `${amount > 0 ? "上分" : "下分"} NT$ ${Math.abs(amount).toLocaleString()} 已成功，新餘額 NT$ ${newBalance.toLocaleString()}`,
        "success",
      );
      setBalanceAmount("");
      setBalanceReason("");

      // 清除前台會員的本地快取，強制下次進入讀取最新金額
      clearWalletCache(userId);
      // 通知前台會員刷新錢包餘額（若會員正開著前台頁面）
      window.dispatchEvent(new CustomEvent("wallet:refresh"));

      // 刷新流水帳
      const { data: txData } = await supabase
        .from("wallet_transactions")
        .select("id, amount, type, description, created_at")
        .eq("user_id", userId)
        .order("created_at", { ascending: false })
        .limit(50);
      setWalletTxs((txData as WalletTx[]) ?? []);

      onUpdated();
    } catch (err) {
      showMsg(err instanceof Error ? err.message : "餘額調整失敗", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleBalanceAdjust = async () => {
    const amount = parseInt(balanceAmount, 10);
    if (Number.isNaN(amount) || amount === 0) {
      showMsg("請輸入有效的金額", "error");
      return;
    }
    const actionText = amount > 0 ? "上分" : "下分";

    // 以右側面板顯示的目前儲值總額為準，確保彈窗說明與面板完全一致
    const current = walletBalance ?? 0;
    const after = current + amount;
    openConfirm(
      `確認${actionText}操作`,
      `會員「${profile?.name || '—'}」目前餘額 NT$ ${current.toLocaleString()}，${actionText} NT$ ${Math.abs(amount).toLocaleString()} 後餘額將變為 NT$ ${after.toLocaleString()}。確定要執行嗎？`,
      executeBalanceAdjust,
      amount < 0
    );
  };

  // === Adjust total earnings ===
  const executeEarningsAdjust = async () => {
    const amount = parseInt(earningsAmount, 10);
    if (Number.isNaN(amount) || amount === 0) {
      showMsg("請輸入有效的金額", "error");
      return;
    }

    setSaving(true);
    try {
      const newEarnings = totalEarnings + amount;
      const { error: upErr } = await supabase
        .from("wallets")
        .update({ total_earnings: newEarnings })
        .eq("user_id", userId);
      if (upErr) throw upErr;

      const { error: txErr } = await supabase.from("wallet_transactions").insert({
        user_id: userId,
        amount,
        type: "admin_adjustment",
        description: earningsReason?.trim() || "管理員手動調整累計收益",
      });
      if (txErr) throw txErr;

      setTotalEarnings(newEarnings);
      showMsg(
        `累計收益已${amount > 0 ? "增加" : "減少"} NT$ ${Math.abs(amount).toLocaleString()}，目前累計收益 NT$ ${newEarnings.toLocaleString()}`,
        "success",
      );
      setEarningsAmount("");
      setEarningsReason("");

      const { data: txData } = await supabase
        .from("wallet_transactions")
        .select("id, amount, type, description, created_at")
        .eq("user_id", userId)
        .order("created_at", { ascending: false })
        .limit(50);
      setWalletTxs((txData as WalletTx[]) ?? []);
      onUpdated();
    } catch (err) {
      showMsg(err instanceof Error ? err.message : "調整失敗", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleEarningsAdjust = () => {
    const amount = parseInt(earningsAmount, 10);
    if (Number.isNaN(amount) || amount === 0) {
      showMsg("請輸入有效的金額", "error");
      return;
    }
    const actionText = amount > 0 ? "增加" : "減少";
    const after = totalEarnings + amount;
    openConfirm(
      `確認調整累計收益`,
      `會員「${profile?.name || '—'}」目前累計收益 NT$ ${totalEarnings.toLocaleString()}，${actionText} NT$ ${Math.abs(amount).toLocaleString()} 後將變為 NT$ ${after.toLocaleString()}。確定要執行嗎？`,
      executeEarningsAdjust,
      amount < 0,
    );
  };

  // === Adjust pending profits ===
  const executePendingAdjust = async () => {
    const amount = parseInt(pendingAmount, 10);
    if (Number.isNaN(amount) || amount === 0) {
      showMsg("請輸入有效的金額", "error");
      return;
    }

    setSaving(true);
    try {
      const newPending = pendingProfits + amount;
      const { error: upErr } = await supabase
        .from("wallets")
        .update({ pending_profits: newPending })
        .eq("user_id", userId);
      if (upErr) throw upErr;

      const { error: txErr } = await supabase.from("wallet_transactions").insert({
        user_id: userId,
        amount,
        type: "admin_adjustment",
        description: pendingReason?.trim() || "管理員手動調整待結算利潤",
      });
      if (txErr) throw txErr;

      setPendingProfits(newPending);
      showMsg(
        `待結算利潤已${amount > 0 ? "增加" : "減少"} NT$ ${Math.abs(amount).toLocaleString()}，目前待結算利潤 NT$ ${newPending.toLocaleString()}`,
        "success",
      );
      setPendingAmount("");
      setPendingReason("");

      const { data: txData } = await supabase
        .from("wallet_transactions")
        .select("id, amount, type, description, created_at")
        .eq("user_id", userId)
        .order("created_at", { ascending: false })
        .limit(50);
      setWalletTxs((txData as WalletTx[]) ?? []);
      onUpdated();
    } catch (err) {
      showMsg(err instanceof Error ? err.message : "調整失敗", "error");
    } finally {
      setSaving(false);
    }
  };

  const handlePendingAdjust = () => {
    const amount = parseInt(pendingAmount, 10);
    if (Number.isNaN(amount) || amount === 0) {
      showMsg("請輸入有效的金額", "error");
      return;
    }
    const actionText = amount > 0 ? "增加" : "減少";
    const after = pendingProfits + amount;
    openConfirm(
      `確認調整待結算利潤`,
      `會員「${profile?.name || '—'}」目前待結算利潤 NT$ ${pendingProfits.toLocaleString()}，${actionText} NT$ ${Math.abs(amount).toLocaleString()} 後將變為 NT$ ${after.toLocaleString()}。確定要執行嗎？`,
      executePendingAdjust,
      amount < 0,
    );
  };

  // Delete single order
  const executeDeleteOrder = async (orderId: string) => {
    setSaving(true);
    try {
      const { error } = await supabase.from("orders").delete().eq("id", orderId);
      if (error) throw error;
      showMsg("訂單已成功刪除", "success");
      await refreshOrdersAndWallet();
    } catch (err) {
      showMsg(err instanceof Error ? err.message : "刪除訂單失敗", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteOrder = (orderId: string, productName: string) => {
    openConfirm(
      "確認刪除訂單",
      `確定要刪除「${productName}」這筆訂單嗎？刪除後數據將無法恢復。`,
      () => executeDeleteOrder(orderId),
      true,
      "確認刪除",
      "取消",
    );
  };

  // Update order progress
  const handleProgressChange = async (orderId: string, newPct: number) => {
    const clamped = Math.max(0, Math.min(100, Math.round(newPct)));
    setOrders((prev) =>
      prev.map((o) => (o.id === orderId ? { ...o, completion_percentage: clamped } : o))
    );
    try {
      const { error } = await supabase.from("orders").update({ completion_percentage: clamped }).eq("id", orderId);
      if (error) throw error;
    } catch (err) {
      showMsg(err instanceof Error ? err.message : "進度更新失敗", "error");
    }
  };

  // Delete member account
  const handleDeleteAccount = () => {
    const memberName = profile?.name || "該會員";
    openConfirm(
      "確認刪除會員帳號",
      `確定要永久刪除此會員帳號（${memberName}）嗎？此操作將無法復原，其錢包餘額與訂單紀錄也將一併清空！`,
      executeDeleteAccount,
      true,
      "確認刪除",
      "取消"
    );
  };

  const executeDeleteAccount = async () => {
    setDeleteLoading(true);
    try {
      const { error: walletErr } = await supabase
        .from("wallets")
        .delete()
        .eq("user_id", userId);
      if (walletErr) console.error("[MemberEditPanel] wallet delete error:", walletErr);

      const { error: txErr } = await supabase
        .from("wallet_transactions")
        .delete()
        .eq("user_id", userId);
      if (txErr) console.error("[MemberEditPanel] wallet_transactions delete error:", txErr);

      const { error: ordersErr } = await supabase
        .from("orders")
        .delete()
        .eq("user_id", userId);
      if (ordersErr) console.error("[MemberEditPanel] orders delete error:", ordersErr);

      const { error: withdrawalsErr } = await supabase
        .from("withdrawals")
        .delete()
        .eq("user_id", userId);
      if (withdrawalsErr) console.error("[MemberEditPanel] withdrawals delete error:", withdrawalsErr);

      const { error: profileErr } = await supabase
        .from("profiles")
        .delete()
        .eq("id", userId);
      if (profileErr) throw new Error(profileErr.message);

      showMsg("該會員帳號已成功刪除", "success");
      setTimeout(() => {
        onClose();
        onUpdated();
      }, 800);
    } catch (err) {
      showMsg(err instanceof Error ? err.message : "刪除失敗", "error");
    } finally {
      setDeleteLoading(false);
    }
  };

  // === Place Order (代客下單) ===
  const handlePlaceOrderImageUpload = async (file: File) => {
    setPoUploading(true);
    setPoMsg("");
    try {
      const ext = file.name.split(".").pop()?.toLowerCase() || "jpg";
      const allowed = ["jpg", "jpeg", "png", "webp"];
      if (!allowed.includes(ext)) {
        setPoMsg("僅支援 .jpg, .png, .webp 格式");
        setPoUploading(false);
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setPoMsg("圖片大小超過 5MB 限制");
        setPoUploading(false);
        return;
      }
      const path = `orders/${crypto.randomUUID()}.${ext}`;
      const { error: upError } = await supabase.storage
        .from("product-images")
        .upload(path, file, { contentType: file.type });
      if (upError) throw upError;
      const { data: urlData } = supabase.storage
        .from("product-images")
        .getPublicUrl(path);
      setPoImageUrl(urlData.publicUrl);
      setPoMsg("圖片上傳成功");
    } catch (err) {
      setPoMsg(`圖片上傳失敗：${err instanceof Error ? err.message : "未知錯誤"}`);
    } finally {
      setPoUploading(false);
    }
  };

  const handlePlaceOrderSubmit = async () => {
    const amountNum = Number(poAmount);
    const sellingNum = Number(poSellingAmount);
    const profitNum = Number(poProfit);
    const extraProfitNum = Number(poExtraProfit);
    const qtyNum = Number(poQuantity);

    if (!poProductName.trim()) {
      setPoMsg("請輸入商品名稱");
      return;
    }
    if (Number.isNaN(amountNum) || amountNum <= 0) {
      setPoMsg("請輸入有效的認購金額");
      return;
    }
    if (Number.isNaN(sellingNum) || sellingNum <= 0) {
      setPoMsg("請輸入有效的銷售價");
      return;
    }
    if (Number.isNaN(qtyNum) || qtyNum < 1) {
      setPoMsg("請輸入有效的下單數量（至少 1）");
      return;
    }

    const dbStatus = poStatus === "上架中" ? "active" : "completed";

    setSaving(true);
    try {
      const returnRate = amountNum > 0 ? Math.round(((sellingNum - amountNum) / amountNum) * 100) : 0;

      const { data: rpcData, error: rpcError } = await supabase.rpc(
        "admin_place_order_v2",
        {
          p_user_id: userId,
          p_product_name: poProductName.trim(),
          p_amount: amountNum,
          p_selling_amount: sellingNum,
          p_quantity: qtyNum,
          p_category: poCategory,
          p_image_url: poImageUrl || null,
          p_status: dbStatus,
          p_type: poCategory,
          p_profit: Number.isNaN(profitNum) || profitNum < 0 ? null : profitNum,
          p_extra_profit: Number.isNaN(extraProfitNum) || extraProfitNum < 0 ? null : extraProfitNum,
        },
      );

      if (rpcError) {
        setPoMsg(`下單失敗：${rpcError.message}`);
        return;
      }

      if (!rpcData || !rpcData.success) {
        setPoMsg(`下單失敗：${rpcData?.error || "未知錯誤"}`);
        return;
      }

      // 補充 return_rate：RPC 未計算此欄位，前台利潤顯示高度依賴它
      try {
        if (rpcData?.order_id) {
          await supabase.from("orders").update({ return_rate: returnRate }).eq("id", rpcData.order_id);
        } else {
          const { data: latestOrder } = await supabase
            .from("orders")
            .select("id")
            .eq("user_id", userId)
            .eq("product_name", poProductName.trim())
            .order("created_at", { ascending: false })
            .limit(1)
            .maybeSingle();
          if (latestOrder) {
            await supabase.from("orders").update({ return_rate: returnRate }).eq("id", latestOrder.id);
          }
        }
      } catch (e) {
        console.error("[MemberEditPanel] 補充 return_rate 失敗:", e);
      }

      // 刷新訂單列表與錢包
      await refreshOrdersAndWallet();

      showMsg("代客下單成功，訂單已建立");
      // Reset form
      setPoProductName("");
      setPoAmount("");
      setPoSellingAmount("");
      setPoProfit("");
      setPoExtraProfit("");
      setPoQuantity("1");
      setPoCategory("trial");
      setPoStatus("上架中");
      setPoImageUrl("");
      setPoMsg("");
      setShowPlaceOrder(false);
    } catch (err) {
      setPoMsg(err instanceof Error ? err.message : "下單失敗");
    } finally {
      setSaving(false);
    }
  };

  // 手動完成訂單（後續結算）
  const handleCompleteOrder = async (orderId: string) => {
    setSaving(true);
    try {
      const { data: rpcData, error: rpcError } = await supabase.rpc(
        "admin_complete_order",
        { p_order_id: orderId },
      );

      if (rpcError) {
        showMsg(`結算失敗：${rpcError.message}`, "error");
        return;
      }

      if (!rpcData || !rpcData.success) {
        showMsg(`結算失敗：${rpcData?.error || "未知錯誤"}`, "error");
        return;
      }

      // ── 扣減該筆訂單對應的待結算利潤（歸零）──
      const completedOrder = orders.find((o) => o.id === orderId);
      if (completedOrder) {
        const profitDeduct =
          ((completedOrder.profit ?? 0) + (completedOrder.extra_profit ?? 0)) *
          (completedOrder.quantity ?? 1);
        if (profitDeduct > 0) {
          try {
            const { data: walletData, error: walletErr } = await supabase
              .from("wallets")
              .select("pending_profits")
              .eq("user_id", userId)
              .maybeSingle();
            if (!walletErr && walletData) {
              const newPending = Math.max(
                0,
                (walletData.pending_profits ?? 0) - profitDeduct,
              );
              await supabase
                .from("wallets")
                .update({ pending_profits: newPending })
                .eq("user_id", userId);
              await supabase.from("wallet_transactions").insert({
                user_id: userId,
                order_id: orderId,
                amount: -profitDeduct,
                type: "profit_settled",
                description: `訂單「${completedOrder.product_name}」完成結算，待結算利潤歸零`,
              });
            }
          } catch (pendingErr) {
            console.error(
              "[MemberEditPanel] 待結算利潤扣減失敗:",
              pendingErr,
            );
            showMsg(
              `訂單已結算，但待結算利潤扣減失敗，請至「人工上下分」手動調整`,
              "error",
            );
          }
        }
      }

      // 更新本地訂單狀態
      setOrders((prev) =>
        prev.map((o) =>
          o.id === orderId
            ? { ...o, status: "completed", completion_percentage: 100 }
            : o,
        ),
      );

      await refreshOrdersAndWallet();

      const refundAmt = (rpcData.refund_amount as number) ?? 0;
      showMsg(
        `訂單已結算完成，返款 NT$ ${refundAmt.toLocaleString()} 至會員錢包`,
        "success",
      );
    } catch (err) {
      showMsg(err instanceof Error ? err.message : "結算失敗", "error");
    } finally {
      setSaving(false);
    }
  };

  const tabs: { key: TabKey; label: string; icon: string }[] = [
    { key: "basic", label: "基本資料", icon: "ri-user-line" },
    { key: "bank", label: "銀行資訊", icon: "ri-bank-card-line" },
    { key: "orders", label: "認購訂單", icon: "ri-file-list-3-line" },
    { key: "balance", label: "人工上下分", icon: "ri-exchange-dollar-line" },
  ];

  const idCards = [
    { key: "id_card_front" as const, url: profile?.id_card_front },
    { key: "id_card_back" as const, url: profile?.id_card_back },
    { key: "id_card_holding" as const, url: profile?.id_card_holding },
  ];

  const hasAnyIdCard = idCards.some((c) => !!c.url);

  return (
    <>
      {/* Overlay + Slide-in Panel */}
      <div className="fixed inset-0 z-[50] flex justify-end">
        <div className="absolute inset-0 bg-black/40" onClick={onClose} />
        <div className="relative bg-white w-full max-w-[480px] h-full overflow-y-auto shadow-xl flex flex-col">
          {/* Header */}
          <div className="sticky top-0 z-10 bg-white border-b border-navy-100 px-6 py-4 flex items-center justify-between">
            <div>
              <h2 className="font-display text-base font-bold text-navy-900">會員詳情管理</h2>
              <p className="font-body text-[11px] text-navy-400 mt-0.5">
                {profile?.name || "載入中..."} · {profile?.username || "—"}
              </p>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-navy-50 text-navy-400 hover:text-navy-700 cursor-pointer"
            >
              <i className="ri-close-line text-lg" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-1 px-4 py-3 border-b border-navy-100 bg-navy-50/30 overflow-x-auto">
            {tabs.map((t) => {
              const isActive = activeTab === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setActiveTab(t.key)}
                  className={`flex items-center gap-1.5 font-body text-xs font-medium px-3 py-2 rounded-lg whitespace-nowrap cursor-pointer ${
                    isActive
                      ? "bg-navy-700 text-white"
                      : "text-navy-500 hover:bg-navy-50 hover:text-navy-700"
                  }`}
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`${t.icon} text-sm`} />
                  </div>
                  {t.label}
                </button>
              );
            })}
          </div>

          {/* Message */}
          {msg && (
            <div className={`mx-4 mt-3 font-body text-sm px-4 py-2.5 rounded-lg border ${
              msgType === "success"
                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                : "bg-red-50 text-red-600 border-red-200"
            }`}>
              {msg}
            </div>
          )}

          {/* Content */}
          <div className="flex-1 p-5">
            {loading ? (
              <div className="flex flex-col items-center gap-3 py-16">
                <div className="w-8 h-8 rounded-full border-2 border-navy-200 border-t-navy-700 animate-spin" />
                <p className="font-body text-sm text-navy-400">載入會員資料中...</p>
              </div>
            ) : (
              <>
                {/* === Basic Info Tab === */}
                {activeTab === "basic" && (
                  <div className="flex flex-col gap-4">
                    <div>
                      <label className="font-body text-xs font-medium text-navy-600 block mb-1.5">姓名（名義）</label>
                      <input
                        type="text"
                        value={formName}
                        onChange={(e) => setFormName(e.target.value)}
                        className="w-full font-body text-sm px-3 py-2.5 rounded-lg border border-navy-200 outline-none focus:border-navy-400 bg-white"
                        placeholder="輸入會員姓名"
                      />
                    </div>
                    <div>
                      <label className="font-body text-xs font-medium text-navy-600 block mb-1.5">手機號碼</label>
                      <input
                        type="tel"
                        value={formPhone}
                        onChange={(e) => setFormPhone(e.target.value)}
                        className="w-full font-body text-sm px-3 py-2.5 rounded-lg border border-navy-200 outline-none focus:border-navy-400 bg-white"
                        placeholder="輸入手機號碼"
                      />
                    </div>
                    <div>
                      <label className="font-body text-xs font-medium text-navy-600 block mb-1.5">會員等級</label>
                      <select
                        value={formLevel}
                        onChange={(e) => setFormLevel(e.target.value)}
                        className="w-full font-body text-sm px-3 py-2.5 rounded-lg border border-navy-200 outline-none focus:border-navy-400 bg-white"
                      >
                        {MEMBERSHIP_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="font-body text-xs font-medium text-navy-600 block mb-1.5">帳號狀態</label>
                      <select
                        value={formStatus}
                        onChange={(e) => setFormStatus(e.target.value)}
                        className="w-full font-body text-sm px-3 py-2.5 rounded-lg border border-navy-200 outline-none focus:border-navy-400 bg-white"
                      >
                        <option value="active">啟用中</option>
                        <option value="suspended">暫停</option>
                        <option value="banned">封鎖</option>
                      </select>
                    </div>

                    {/* Login Password */}
                    <div className="border border-navy-100 rounded-xl p-4 flex flex-col gap-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-body text-xs font-medium text-navy-600">變更登入密碼</p>
                          <p className="font-display text-base font-bold text-navy-900 mt-0.5">
                            {formLoginPassword ? "準備變更" : "未變更"}
                          </p>
                        </div>
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          formLoginPassword ? "bg-amber-50 text-amber-600" : "bg-stone-100 text-stone-400"
                        }`}>
                          <i className="ri-key-line text-lg" />
                        </div>
                      </div>
                      <div>
                        <label className="font-body text-xs font-medium text-navy-600 block mb-1.5">目前登入密碼</label>
                        <input
                          type="text"
                          value={formPlainPassword}
                          readOnly
                          className="w-full font-body text-sm px-3 py-2.5 rounded-lg border border-navy-200 bg-navy-50 text-navy-700 outline-none cursor-default"
                        />
                        <p className="font-body text-[11px] text-navy-400 mt-1">
                          僅供管理員查看，此處顯示會員目前使用的登入密碼明文。若為空代表尚未設定或舊系統無此紀錄。
                        </p>
                      </div>
                      <div className="relative">
                        <input
                          type={showLoginPassword ? "text" : "password"}
                          value={formLoginPassword}
                          onChange={(e) => setFormLoginPassword(e.target.value)}
                          className="w-full font-body text-sm px-3 py-2.5 rounded-lg border border-navy-200 outline-none focus:border-navy-400 bg-white pr-10"
                          placeholder="輸入新登入密碼（至少 6 位）"
                        />
                        <button
                          type="button"
                          onClick={() => setShowLoginPassword((v) => !v)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-md hover:bg-navy-50 text-navy-400 hover:text-navy-600 cursor-pointer"
                        >
                          <i className={showLoginPassword ? "ri-eye-off-line" : "ri-eye-line"} />
                        </button>
                      </div>
                      <p className="font-body text-[11px] text-navy-400">
                        限輸入至少 6 位字元，管理員可強制重置該會員登入密碼
                      </p>
                      <button
                        onClick={handleUpdateLoginPassword}
                        disabled={saving || !formLoginPassword}
                        className="w-full font-body text-sm font-semibold px-4 py-2.5 rounded-lg bg-navy-700 text-white hover:bg-navy-800 disabled:opacity-50 cursor-pointer whitespace-nowrap"
                      >
                        {saving ? "更新中..." : "更新登入密碼"}
                      </button>
                    </div>

                    {/* Withdraw Password */}
                    <div className="border border-navy-100 rounded-xl p-4 flex flex-col gap-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-body text-xs font-medium text-navy-600">提款密碼</p>
                          <p className="font-display text-base font-bold text-navy-900 mt-0.5">
                            {formWithdrawPassword ? "已設置" : "未設置"}
                          </p>
                        </div>
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          formWithdrawPassword ? "bg-emerald-50 text-emerald-600" : "bg-stone-100 text-stone-400"
                        }`}>
                          <i className={formWithdrawPassword ? "ri-shield-check-line text-lg" : "ri-shield-line text-lg"} />
                        </div>
                      </div>
                      <div>
                        <input
                          type="text"
                          value={formWithdrawPassword}
                          onChange={(e) => {
                            const val = e.target.value.replace(/[^a-zA-Z0-9]/g, "").slice(0, 6);
                            setFormWithdrawPassword(val);
                          }}
                          className="w-full font-body text-sm px-3 py-2.5 rounded-lg border border-navy-200 outline-none focus:border-navy-400 bg-white"
                          placeholder="4–6 位英文或數字，留空則清除"
                          maxLength={6}
                        />
                        <p className="font-body text-[11px] text-navy-400 mt-1">
                          限輸入英文與數字，長度 4–6 位
                        </p>
                      </div>
                      <button
                        onClick={handleSaveWithdrawPassword}
                        disabled={saving}
                        className="w-full font-body text-sm font-semibold px-4 py-2.5 rounded-lg bg-navy-700 text-white hover:bg-navy-800 disabled:opacity-50 cursor-pointer whitespace-nowrap"
                      >
                        {saving ? "儲存中..." : "儲存提款密碼"}
                      </button>
                    </div>

                    {/* Admin Toggle */}
                    <div className="border border-navy-100 rounded-xl p-4 flex flex-col gap-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-body text-xs font-medium text-navy-600">管理員權限</p>
                          <p className="font-display text-base font-bold text-navy-900 mt-0.5">
                            {formIsAdmin ? "已開啟" : "未開啟"}
                          </p>
                        </div>
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          formIsAdmin ? "bg-navy-700 text-white" : "bg-stone-100 text-stone-400"
                        }`}>
                          <i className={formIsAdmin ? "ri-shield-check-line text-lg" : "ri-shield-line text-lg"} />
                        </div>
                      </div>
                      {formIsAdmin ? (
                        <button
                          onClick={() => handleAdminUpdate(false)}
                          disabled={saving}
                          className="w-full font-body text-sm font-medium px-4 py-2.5 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50 cursor-pointer whitespace-nowrap"
                        >
                          撤銷管理員權限
                        </button>
                      ) : (
                        <button
                          onClick={() => handleAdminUpdate(true)}
                          disabled={saving}
                          className="w-full font-body text-sm font-semibold px-4 py-2.5 rounded-lg bg-navy-700 text-white hover:bg-navy-800 disabled:opacity-50 cursor-pointer whitespace-nowrap"
                        >
                          設為管理員
                        </button>
                      )}
                    </div>

                    <div className="pt-2">
                      <button
                        onClick={handleSaveBasic}
                        disabled={saving}
                        className="w-full font-body text-sm font-semibold px-4 py-2.5 rounded-lg bg-navy-700 text-white hover:bg-navy-800 disabled:opacity-50 cursor-pointer whitespace-nowrap"
                      >
                        {saving ? "儲存中..." : "儲存基本資料"}
                      </button>
                    </div>
                  </div>
                )}

                {/* === Bank Info Tab === */}
                {activeTab === "bank" && (
                  <div className="flex flex-col gap-4">
                    {/* Verification Status */}
                    <div className="border border-navy-100 rounded-xl p-4 flex flex-col gap-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-body text-xs font-medium text-navy-600">實名認證狀態</p>
                          <p className="font-display text-lg font-bold text-navy-900 mt-0.5">
                            {formVerificationStatus === "approved"
                              ? "已認證"
                              : formVerificationStatus === "pending"
                                ? "審核中"
                                : formVerificationStatus === "rejected"
                                  ? "已拒絕"
                                  : "未認證"}
                          </p>
                        </div>
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          formVerificationStatus === "approved"
                            ? "bg-emerald-50 text-emerald-600"
                            : formVerificationStatus === "pending"
                              ? "bg-amber-50 text-amber-600"
                              : formVerificationStatus === "rejected"
                                ? "bg-red-50 text-red-500"
                                : "bg-stone-100 text-stone-400"
                        }`}>
                          <i className={
                            formVerificationStatus === "approved"
                              ? "ri-checkbox-circle-line text-lg"
                              : formVerificationStatus === "pending"
                                ? "ri-time-line text-lg"
                                : formVerificationStatus === "rejected"
                                  ? "ri-close-circle-line text-lg"
                                  : "ri-error-warning-line text-lg"
                          } />
                        </div>
                      </div>

                      {formVerificationStatus === "pending" && (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleVerificationUpdate("approved")}
                            disabled={saving}
                            className="flex-1 font-body text-sm font-semibold px-4 py-2.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 cursor-pointer whitespace-nowrap"
                          >
                            通過審核
                          </button>
                          <button
                            onClick={() => handleVerificationUpdate("rejected")}
                            disabled={saving}
                            className="flex-1 font-body text-sm font-semibold px-4 py-2.5 rounded-lg bg-red-500 text-white hover:bg-red-600 disabled:opacity-50 cursor-pointer whitespace-nowrap"
                          >
                            拒絕認證
                          </button>
                        </div>
                      )}

                      {formVerificationStatus === "approved" && (
                        <button
                          onClick={() => handleVerificationUpdate("unverified")}
                          disabled={saving}
                          className="w-full font-body text-sm font-medium px-4 py-2.5 rounded-lg border border-navy-200 text-navy-600 hover:bg-navy-50 disabled:opacity-50 cursor-pointer whitespace-nowrap"
                        >
                          撤銷認證（設為未認證）
                        </button>
                      )}

                      {formVerificationStatus === "rejected" && (
                        <button
                          onClick={() => handleVerificationUpdate("approved")}
                          disabled={saving}
                          className="w-full font-body text-sm font-semibold px-4 py-2.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 cursor-pointer whitespace-nowrap"
                        >
                          重新通過審核
                        </button>
                      )}
                    </div>

                    {/* ID Card Photos */}
                    {hasAnyIdCard && (
                      <div className="border border-navy-100 rounded-xl p-4 flex flex-col gap-3">
                        <p className="font-body text-xs font-medium text-navy-600">身分證照片</p>
                        <div className="grid grid-cols-3 gap-2">
                          {idCards.map((card) => (
                            <div key={card.key} className="flex flex-col gap-1.5">
                              {card.url ? (
                                <button
                                  onClick={() => setLightboxImage(card.url)}
                                  className="relative w-full aspect-[3/4] rounded-lg overflow-hidden border border-navy-200 bg-navy-50 cursor-pointer group"
                                >
                                  <img
                                    src={card.url}
                                    alt={idCardLabels[card.key]}
                                    className="w-full h-full object-cover"
                                    loading="lazy"
                                    decoding="async"
                                  />
                                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                                    <i className="ri-zoom-in-line text-white opacity-0 group-hover:opacity-100 text-xl" />
                                  </div>
                                </button>
                              ) : (
                                <div className="w-full aspect-[3/4] rounded-lg border border-dashed border-navy-200 bg-navy-50/50 flex flex-col items-center justify-center gap-1">
                                  <i className="ri-image-line text-navy-300 text-lg" />
                                  <span className="font-body text-[10px] text-navy-400">未上傳</span>
                                </div>
                              )}
                              <span className="font-body text-[10px] text-navy-500 text-center leading-tight">
                                {idCardLabels[card.key]}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div>
                      <label className="font-body text-xs font-medium text-navy-600 block mb-1.5">銀行名稱</label>
                      <input
                        type="text"
                        value={formBankName}
                        onChange={(e) => setFormBankName(e.target.value)}
                        className="w-full font-body text-sm px-3 py-2.5 rounded-lg border border-navy-200 outline-none focus:border-navy-400 bg-white"
                        placeholder="例如：台灣銀行"
                      />
                    </div>
                    <div>
                      <label className="font-body text-xs font-medium text-navy-600 block mb-1.5">分行代碼</label>
                      <input
                        type="text"
                        value={formBranchCode}
                        onChange={(e) => setFormBranchCode(e.target.value)}
                        className="w-full font-body text-sm px-3 py-2.5 rounded-lg border border-navy-200 outline-none focus:border-navy-400 bg-white"
                        placeholder="例如：004"
                      />
                    </div>
                    <div>
                      <label className="font-body text-xs font-medium text-navy-600 block mb-1.5">銀行帳號</label>
                      <input
                        type="text"
                        value={formBankAccount}
                        onChange={(e) => setFormBankAccount(e.target.value)}
                        className="w-full font-body text-sm px-3 py-2.5 rounded-lg border border-navy-200 outline-none focus:border-navy-400 bg-white"
                        placeholder="輸入銀行帳號"
                      />
                    </div>
                    <div>
                      <label className="font-body text-xs font-medium text-navy-600 block mb-1.5">戶名</label>
                      <input
                        type="text"
                        value={formAccountHolder}
                        onChange={(e) => setFormAccountHolder(e.target.value)}
                        className="w-full font-body text-sm px-3 py-2.5 rounded-lg border border-navy-200 outline-none focus:border-navy-400 bg-white"
                        placeholder="輸入戶名"
                      />
                    </div>
                    <div className="pt-2">
                      <button
                        onClick={() => {
                          openConfirm(
                            "確認更新銀行資訊",
                            `即將更新「${profile?.name || '—'}」的銀行帳戶資料。確定要儲存嗎？`,
                            handleSaveBank
                          );
                        }}
                        disabled={saving}
                        className="w-full font-body text-sm font-semibold px-4 py-2.5 rounded-lg bg-navy-700 text-white hover:bg-navy-800 disabled:opacity-50 cursor-pointer whitespace-nowrap"
                      >
                        {saving ? "儲存中..." : "儲存銀行資訊"}
                      </button>
                    </div>
                  </div>
                )}

                {/* === Orders Tab === */}
                {activeTab === "orders" && (
                  <div className="flex flex-col gap-4">
                    <button
                      onClick={() => setShowPlaceOrder(true)}
                      disabled={saving}
                      className="w-full flex items-center justify-center gap-2 font-body text-sm font-semibold px-4 py-2.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-add-line" />
                      手動代客下單
                    </button>

                    {orders.length === 0 ? (
                      <div className="flex flex-col items-center gap-3 py-12">
                        <div className="w-12 h-12 rounded-full bg-navy-50 flex items-center justify-center">
                          <i className="ri-file-list-3-line text-navy-300 text-xl" />
                        </div>
                        <p className="font-body text-sm text-navy-400">該會員暫無認購記錄</p>
                      </div>
                    ) : (
                      orders.map((o) => (
                        <div
                          key={o.id}
                          className="border border-navy-100 rounded-xl p-4 flex flex-col gap-3"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-3">
                              {o.image_url && (
                                <div className="w-12 h-12 rounded-lg overflow-hidden border border-navy-200 bg-navy-50 shrink-0">
                                  <img src={o.image_url} alt={o.product_name} className="w-full h-full object-cover" loading="lazy" decoding="async" />
                                </div>
                              )}
                              <div>
                                <p className="font-body text-sm font-medium text-navy-900">{o.product_name}</p>
                                <p className="font-body text-[11px] text-navy-400 mt-0.5">
                                  認購金額 NT$ {o.amount.toLocaleString()}
                                  {o.quantity && o.quantity > 1 ? ` × ${o.quantity} = NT$ ${(o.amount * o.quantity).toLocaleString()}` : ""}
                                  {o.selling_amount != null && ` · 銷售價 NT$ ${o.selling_amount.toLocaleString()}`}
                                  {o.profit != null && o.profit > 0 && ` · 商品利潤 NT$ ${o.profit.toLocaleString()}`}
                                  {o.extra_profit != null && o.extra_profit > 0 && ` · 額外分潤 NT$ ${o.extra_profit.toLocaleString()}`}
                                  · {o.type === "vip" ? "VIP 區" : "體驗區"}
                                  {o.quantity && o.quantity > 1 ? ` · ${o.quantity} 件` : ""}
                                  {o.admin_created && (
                                    <span className="ml-1.5 font-body text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-50 text-amber-600 border border-amber-200">管理員代下</span>
                                  )}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-1.5 shrink-0">
                              <button
                                onClick={() => handleDeleteOrder(o.id, o.product_name)}
                                disabled={saving}
                                className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-red-50 text-red-400 hover:text-red-600 transition-colors cursor-pointer disabled:opacity-40"
                                title="刪除訂單"
                              >
                                <i className="ri-delete-bin-line text-sm" />
                              </button>
                              <span className={`font-body text-[10px] font-semibold px-2 py-1 rounded-full border ${
                                o.status === "進行中" || o.status === "active"
                                  ? "bg-emerald-50 text-emerald-600 border-emerald-200"
                                  : "bg-navy-50 text-navy-500 border-navy-200"
                              }`}>
                                {o.status === "進行中" || o.status === "active" ? "進行中" : "已完成"}
                              </span>
                            </div>
                          </div>

                          <div>
                            <div className="flex items-center justify-between mb-1.5">
                              <label className="font-body text-xs font-medium text-navy-600">認購完成度</label>
                              <span className="font-body text-xs font-bold text-navy-900">
                                {o.completion_percentage || 0}%
                              </span>
                            </div>
                            <input
                              type="range"
                              min={0}
                              max={100}
                              value={o.completion_percentage || 0}
                              onChange={(e) => handleProgressChange(o.id, Number(e.target.value))}
                              className="w-full h-2 rounded-full bg-navy-100 accent-navy-700 cursor-pointer"
                            />
                            <div className="flex justify-between mt-1">
                              <span className="font-body text-[10px] text-navy-400">0%</span>
                              <span className="font-body text-[10px] text-navy-400">100%</span>
                            </div>
                          </div>

                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              min={0}
                              max={100}
                              value={o.completion_percentage || 0}
                              onChange={(e) => handleProgressChange(o.id, Number(e.target.value))}
                              className="w-20 font-body text-sm px-2 py-1.5 rounded-lg border border-navy-200 outline-none focus:border-navy-400 text-center"
                            />
                            <span className="font-body text-xs text-navy-500">%</span>
                          </div>

                          {/* 完成結算按鈕：僅對進行中的訂單顯示 */}
                          {o.status !== "completed" && (
                            <button
                              onClick={() => {
                                const refundTotal = (o.amount + (o.profit ?? 0) + (o.extra_profit ?? 0)) * (o.quantity ?? 1);
                                openConfirm(
                                  "確認完成結算",
                                  `確定要將「${o.product_name}」標記為已完成嗎？系統將自動返款（成本＋利潤＋額外分潤）總額 NT$ ${refundTotal.toLocaleString()} 至會員錢包。`,
                                  () => handleCompleteOrder(o.id),
                                  false,
                                  "確認結算",
                                  "取消",
                                );
                              }}
                              disabled={saving}
                              className="mt-1 w-full font-body text-xs font-semibold px-3 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 cursor-pointer whitespace-nowrap"
                            >
                              {saving ? "處理中..." : `完成結算（返款 NT$ ${((o.amount + (o.profit ?? 0) + (o.extra_profit ?? 0)) * (o.quantity ?? 1)).toLocaleString()}）`}
                            </button>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                )}

                {/* === Balance Tab === */}
                {activeTab === "balance" && (
                  <div className="flex flex-col gap-4">
                    <div className="bg-navy-50 rounded-xl p-4 flex items-center justify-between">
                      <div>
                        <p className="font-body text-[11px] text-navy-400">目前錢包餘額</p>
                        <p className="font-display text-xl font-bold text-navy-900">
                          NT$ {(walletBalance ?? 0).toLocaleString()}
                        </p>
                        {walletBalance === null && (
                          <p className="font-body text-[11px] text-amber-600 mt-0.5">
                            錢包尚未開通，執行上下分時將自動初始化為 NT$ 50,000
                          </p>
                        )}
                      </div>
                      <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center">
                        <i className="ri-wallet-3-line text-navy-600 text-lg" />
                      </div>
                    </div>

                    <div>
                      <label className="font-body text-xs font-medium text-navy-600 block mb-1.5">
                        增減金額（正數 = 上分，負數 = 下分）
                      </label>
                      <input
                        type="number"
                        value={balanceAmount}
                        onChange={(e) => setBalanceAmount(e.target.value)}
                        className="w-full font-body text-sm px-3 py-2.5 rounded-lg border border-navy-200 outline-none focus:border-navy-400 bg-white"
                        placeholder="例如：+5000 或 -2000"
                      />
                      <p className="font-body text-[11px] text-navy-400 mt-1">
                        輸入正數增加餘額，輸入負數減少餘額
                      </p>
                    </div>

                    <div>
                      <label className="font-body text-xs font-medium text-navy-600 block mb-1.5">操作備註（選填）</label>
                      <input
                        type="text"
                        value={balanceReason}
                        onChange={(e) => setBalanceReason(e.target.value)}
                        className="w-full font-body text-sm px-3 py-2.5 rounded-lg border border-navy-200 outline-none focus:border-navy-400 bg-white"
                        placeholder="例如：活動獎勵、手動補償"
                      />
                    </div>

                    <div className="pt-2">
                      <button
                        onClick={handleBalanceAdjust}
                        disabled={saving || !balanceAmount}
                        className="w-full font-body text-sm font-semibold px-4 py-2.5 rounded-lg bg-navy-700 text-white hover:bg-navy-800 disabled:opacity-50 cursor-pointer whitespace-nowrap"
                      >
                        {saving ? "處理中..." : "執行餘額調整"}
                      </button>
                    </div>

                    {/* 調整累計收益 */}
                    <div className="border border-navy-100 rounded-xl p-4 flex flex-col gap-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-body text-[11px] text-navy-400">目前累計收益</p>
                          <p className="font-display text-lg font-bold text-navy-900">
                            NT$ {totalEarnings.toLocaleString()}
                          </p>
                        </div>
                        <div className="w-10 h-10 rounded-full bg-navy-50 flex items-center justify-center">
                          <i className="ri-coins-line text-navy-600 text-lg" />
                        </div>
                      </div>
                      <div>
                        <label className="font-body text-xs font-medium text-navy-600 block mb-1.5">
                          調整累計收益（正數 = 增加，負數 = 減少）
                        </label>
                        <input
                          type="number"
                          value={earningsAmount}
                          onChange={(e) => setEarningsAmount(e.target.value)}
                          className="w-full font-body text-sm px-3 py-2.5 rounded-lg border border-navy-200 outline-none focus:border-navy-400 bg-white"
                          placeholder="例如：+5000 或 -2000"
                        />
                      </div>
                      <div>
                        <label className="font-body text-xs font-medium text-navy-600 block mb-1.5">操作備註（選填）</label>
                        <input
                          type="text"
                          value={earningsReason}
                          onChange={(e) => setEarningsReason(e.target.value)}
                          className="w-full font-body text-sm px-3 py-2.5 rounded-lg border border-navy-200 outline-none focus:border-navy-400 bg-white"
                          placeholder="例如：活動獎勵分潤"
                        />
                      </div>
                      <button
                        onClick={handleEarningsAdjust}
                        disabled={saving || !earningsAmount}
                        className="w-full font-body text-sm font-semibold px-4 py-2.5 rounded-lg bg-navy-700 text-white hover:bg-navy-800 disabled:opacity-50 cursor-pointer whitespace-nowrap"
                      >
                        {saving ? "處理中..." : "儲存累計收益調整"}
                      </button>
                    </div>

                    {/* 調整待結算利潤 */}
                    <div className="border border-navy-100 rounded-xl p-4 flex flex-col gap-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-body text-[11px] text-navy-400">目前待結算利潤</p>
                          <p className="font-display text-lg font-bold text-navy-900">
                            NT$ {pendingProfits.toLocaleString()}
                          </p>
                        </div>
                        <div className="w-10 h-10 rounded-full bg-navy-50 flex items-center justify-center">
                          <i className="ri-time-line text-navy-600 text-lg" />
                        </div>
                      </div>
                      <div>
                        <label className="font-body text-xs font-medium text-navy-600 block mb-1.5">
                          調整待結算利潤（正數 = 增加，負數 = 減少）
                        </label>
                        <input
                          type="number"
                          value={pendingAmount}
                          onChange={(e) => setPendingAmount(e.target.value)}
                          className="w-full font-body text-sm px-3 py-2.5 rounded-lg border border-navy-200 outline-none focus:border-navy-400 bg-white"
                          placeholder="例如：+3000 或 -1000"
                        />
                      </div>
                      <div>
                        <label className="font-body text-xs font-medium text-navy-600 block mb-1.5">操作備註（選填）</label>
                        <input
                          type="text"
                          value={pendingReason}
                          onChange={(e) => setPendingReason(e.target.value)}
                          className="w-full font-body text-sm px-3 py-2.5 rounded-lg border border-navy-200 outline-none focus:border-navy-400 bg-white"
                          placeholder="例如：結算後修正"
                        />
                      </div>
                      <button
                        onClick={handlePendingAdjust}
                        disabled={saving || !pendingAmount}
                        className="w-full font-body text-sm font-semibold px-4 py-2.5 rounded-lg bg-navy-700 text-white hover:bg-navy-800 disabled:opacity-50 cursor-pointer whitespace-nowrap"
                      >
                        {saving ? "處理中..." : "儲存待結算利潤調整"}
                      </button>
                    </div>

                    {/* 近期流水帳 */}
                    {walletTxs.length > 0 && (
                      <div className="border border-navy-100 rounded-xl overflow-hidden mt-2">
                        <div className="px-4 py-3 border-b border-navy-100 bg-navy-50/30">
                          <p className="font-body text-xs font-semibold text-navy-700">近期錢包流水帳</p>
                        </div>
                        <div className="divide-y divide-navy-100">
                          {walletTxs.slice(0, 10).map((tx) => (
                            <div key={tx.id} className="px-4 py-3 flex items-center justify-between">
                              <div>
                                <p className="font-body text-xs font-medium text-navy-800">
                                  {tx.description || "餘額調整"}
                                </p>
                                <p className="font-body text-[10px] text-navy-400 mt-0.5">
                                  {new Date(tx.created_at).toLocaleString("zh-TW")} · {tx.type}
                                </p>
                              </div>
                              <span className={`font-body text-sm font-semibold ${tx.amount >= 0 ? "text-emerald-600" : "text-red-500"}`}>
                                {tx.amount >= 0 ? "+" : ""}NT$ {tx.amount.toLocaleString()}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {/* === Danger Zone === */}
            {!loading && profile && (
              <div className="mt-8 pt-6 border-t border-red-100">
                <div className="border border-red-200 rounded-xl p-4 bg-red-50/30 flex flex-col gap-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
                      <i className="ri-delete-bin-line text-red-500" />
                    </div>
                    <div>
                      <p className="font-body text-sm font-semibold text-red-600">刪除會員帳號</p>
                      <p className="font-body text-[11px] text-red-400">此操作無法復原，所有資料將永久清空</p>
                    </div>
                  </div>
                  <button
                    onClick={handleDeleteAccount}
                    disabled={deleteLoading}
                    className="w-full font-body text-sm font-semibold px-4 py-2.5 rounded-lg bg-red-500 text-white hover:bg-red-600 disabled:opacity-50 cursor-pointer whitespace-nowrap"
                  >
                    {deleteLoading ? "刪除中..." : "刪除會員帳號"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxImage && (
        <div
          className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-6"
          onClick={() => setLightboxImage(null)}
        >
          <button
            onClick={() => setLightboxImage(null)}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white cursor-pointer"
          >
            <i className="ri-close-line text-xl" />
          </button>
          <img
            src={lightboxImage}
            alt="身分證照片預覽"
            className="max-w-full max-h-full rounded-lg object-contain"
            loading="lazy"
            decoding="async"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* Place Order Modal */}
      {showPlaceOrder && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 px-4">
          <div className="bg-white rounded-2xl border border-navy-100 shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-5 border-b border-navy-100 flex items-center justify-between">
              <h3 className="font-display text-base font-bold text-navy-900">手動代客下單</h3>
              <button
                onClick={() => { setShowPlaceOrder(false); setPoMsg(""); }}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-navy-50 text-navy-400 hover:text-navy-700 cursor-pointer"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <div className="p-6 flex flex-col gap-4">
              {poMsg && (
                <p className={`font-body text-sm ${poMsg.includes("成功") ? "text-emerald-600" : "text-red-500"}`}>
                  {poMsg}
                </p>
              )}
              <div>
                <label className="font-body text-xs font-medium text-navy-700 mb-1 block">所屬區域 <span className="text-red-400">*</span></label>
                <div className="flex gap-2">
                  {(["trial", "vip"] as const).map((z) => (
                    <button
                      key={z}
                      onClick={() => setPoCategory(z)}
                      className={`flex-1 py-2 rounded-lg font-body text-sm cursor-pointer ${
                        poCategory === z
                          ? "bg-navy-700 text-white"
                          : "border border-navy-200 text-navy-600 hover:border-navy-400"
                      }`}
                    >
                      {z === "trial" ? "體驗區" : "VIP 區"}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="font-body text-xs font-medium text-navy-700 mb-1 block">
                  商品名稱 <span className="text-red-400">*</span>
                </label>
                <input
                  value={poProductName}
                  onChange={(e) => setPoProductName(e.target.value)}
                  placeholder="例如：限量潮牌球鞋"
                  className="w-full font-body text-sm px-3 py-2.5 rounded-lg border border-navy-200 focus:border-navy-500 outline-none"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col gap-3">
                  <div>
                    <label className="font-body text-xs font-medium text-navy-700 mb-1 block">
                      認購金額（成本） <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="number"
                      value={poAmount}
                      onChange={(e) => setPoAmount(e.target.value)}
                      placeholder="12800"
                      className="w-full font-body text-sm px-3 py-2.5 rounded-lg border border-navy-200 focus:border-navy-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="font-body text-xs font-medium text-navy-700 mb-1 block">
                      銷售價 <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="number"
                      value={poSellingAmount}
                      onChange={(e) => setPoSellingAmount(e.target.value)}
                      placeholder="15800"
                      className="w-full font-body text-sm px-3 py-2.5 rounded-lg border border-navy-200 focus:border-navy-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="font-body text-xs font-medium text-navy-700 mb-1 block">
                      商品利潤
                    </label>
                    <input
                      type="number"
                      value={poProfit}
                      onChange={(e) => setPoProfit(e.target.value)}
                      placeholder="3000"
                      className="w-full font-body text-sm px-3 py-2.5 rounded-lg border border-navy-200 focus:border-navy-500 outline-none"
                    />
                  </div>
                </div>
                <div className="flex flex-col gap-3">
                  <div>
                    <label className="font-body text-xs font-medium text-navy-700 mb-1 block">
                      下單數量 <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="number"
                      min={1}
                      value={poQuantity}
                      onChange={(e) => setPoQuantity(e.target.value)}
                      placeholder="1"
                      className="w-full font-body text-sm px-3 py-2.5 rounded-lg border border-navy-200 focus:border-navy-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="font-body text-xs font-medium text-navy-700 mb-1 block">
                      商品狀態進度 <span className="text-red-400">*</span>
                    </label>
                    <div className="flex gap-2">
                      {(["上架中", "已完成"] as const).map((s) => (
                        <button
                          key={s}
                          onClick={() => setPoStatus(s)}
                          className={`flex-1 py-2 rounded-lg font-body text-sm cursor-pointer ${
                            poStatus === s
                              ? "bg-navy-700 text-white"
                              : "border border-navy-200 text-navy-600 hover:border-navy-400"
                          }`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="font-body text-xs font-medium text-navy-700 mb-1 block">
                      額外分潤
                    </label>
                    <input
                      type="number"
                      value={poExtraProfit}
                      onChange={(e) => setPoExtraProfit(e.target.value)}
                      placeholder="500"
                      className="w-full font-body text-sm px-3 py-2.5 rounded-lg border border-navy-200 focus:border-navy-500 outline-none"
                    />
                  </div>
                </div>
              </div>
              <div>
                <label className="font-body text-xs font-medium text-navy-700 mb-1 block">商品圖片上傳</label>
                <div className="flex items-center gap-3">
                  <label className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 border-dashed transition-colors ${
                    poUploading
                      ? "border-navy-200 bg-navy-50 cursor-not-allowed"
                      : "border-navy-200 hover:border-navy-400 hover:bg-navy-50 cursor-pointer"
                  }`}>
                    <i className={`ri-upload-cloud-2-line ${poUploading ? "text-navy-300" : "text-navy-400"}`} />
                    <span className={`font-body text-sm ${poUploading ? "text-navy-300" : "text-navy-500"}`}>
                      {poUploading ? "上傳中..." : "點擊或拖曳上傳 .jpg / .png / .webp"}
                    </span>
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handlePlaceOrderImageUpload(file);
                        e.target.value = "";
                      }}
                      disabled={poUploading}
                    />
                  </label>
                </div>
                {poImageUrl && (
                  <div className="mt-3 w-full h-32 rounded-lg border border-navy-200 overflow-hidden bg-navy-50">
                    <img src={poImageUrl} alt="預覽" className="w-full h-full object-contain" loading="lazy" decoding="async" />
                  </div>
                )}
              </div>
            </div>
            <div className="px-6 py-4 border-t border-navy-100 flex justify-end gap-2">
              <button
                onClick={() => { setShowPlaceOrder(false); setPoMsg(""); }}
                className="font-body text-sm font-medium px-4 py-2.5 rounded-lg border border-navy-200 text-navy-600 hover:bg-navy-50 cursor-pointer"
              >
                取消
              </button>
              <button
                onClick={handlePlaceOrderSubmit}
                disabled={saving || poUploading}
                className="font-body text-sm font-semibold px-5 py-2.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {saving ? "處理中..." : "確認下單"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmOpen}
        title={confirmTitle}
        message={confirmMessage}
        confirmLabel={confirmLabel}
        cancelLabel={confirmCancelLabel}
        onConfirm={() => {
          setConfirmOpen(false);
          confirmAction?.();
        }}
        onCancel={() => setConfirmOpen(false)}
        danger={confirmDanger}
      />
    </>
  );
}